/**
 * The Testing workspace's ONE primary action.
 *
 * Before this existed, "what should I press?" was answered by six booleans evaluated inline in the
 * header, and Run / Stop were each rendered in four different places with their own copies of the
 * conditions.
 *
 * There is no approval gate: the backend removed POST /api/plans/approve and /execute validates the
 * stored execution contract at run time, so Run means run. A contract that fails validation comes
 * back as a read-only 409 note, not a button.
 *
 * Kept pure and exhaustive so the header renders exactly one primary button, the rule is unit
 * testable, and secondary affordances can be derived from the same state rather than re-deduced.
 */

export type PrimaryActionKind = "stop" | "run" | "waiting" | "none";

export interface TestingActionState {
	/** A session with a real (server-side) plan is selected. */
	hasPlan: boolean;
	/** The plan body is a runnable spec — legacy step-array plans can no longer execute. */
	planExecutable: boolean;
	/**
	 * A legacy plan still carrying `rejected` (the status remains in the API enum even though the
	 * frontend can no longer set it). Such a plan must be re-planned in chat before it can run.
	 */
	planRejected: boolean;
	/** A run/heal is streaming for the SELECTED session. */
	runningHere: boolean;
	/** A run/heal is streaming, but it belongs to a DIFFERENT session. */
	runningElsewhere: boolean;
	/** The planner is streaming a proposal. */
	planningActive: boolean;
	/** There is a live or reconnected server run that can be aborted. */
	canStop: boolean;
	/** Stop was pressed and the run has not wound down yet. */
	stopping: boolean;
}

export interface PrimaryAction {
	kind: PrimaryActionKind;
	/** Button label, or the reason nothing can be pressed when `kind` is "none"/"waiting". */
	label: string;
	/** Label for a narrow header; the full label stays in the tooltip and the accessible name. */
	shortLabel: string;
	/** Label while the action's request is in flight. */
	busyLabel?: string;
	busy: boolean;
	disabled: boolean;
	/** Longer explanation for the button's tooltip / the disabled state's helper text. */
	hint: string;
}

/**
 * Resolves the single primary action for the current state, in strict priority order:
 * stop a live run → run a runnable plan → explain why not.
 */
export function resolvePrimaryAction(state: TestingActionState): PrimaryAction {
	// 1. Stopping a run always outranks starting one.
	if (state.runningHere && state.canStop) {
		return {
			kind: "stop",
			label: "Stop",
			shortLabel: "Stop",
			busyLabel: "Stopping…",
			busy: state.stopping,
			disabled: state.stopping,
			hint: "Stop the run executing on the server",
		};
	}

	// 2. A run is in flight for this session but there is no run id to abort yet.
	if (state.runningHere) {
		return {
			kind: "waiting",
			label: "Starting…",
			shortLabel: "Starting…",
			busy: true,
			disabled: true,
			hint: "The run is starting — Stop becomes available once the server assigns a run id",
		};
	}

	// 3. Another session owns the active run. The user may browse freely, but a second concurrent
	//    run is not offered (the service drives one browser at a time).
	if (state.runningElsewhere) {
		return {
			kind: "none",
			label: "Run",
			shortLabel: "Run",
			busy: false,
			disabled: true,
			hint: "Another test is running. You can keep planning here; Run frees up when it finishes.",
		};
	}

	if (!state.hasPlan) {
		return {
			kind: "none",
			label: "Run",
			shortLabel: "Run",
			busy: false,
			disabled: true,
			hint: "Describe a test in chat — the agent proposes a plan you can run",
		};
	}

	if (state.planRejected) {
		return {
			kind: "none",
			label: "Run",
			shortLabel: "Run",
			busy: false,
			disabled: true,
			hint: "This plan was rejected. Describe the change in chat to replace it.",
		};
	}

	if (!state.planExecutable) {
		return {
			kind: "none",
			label: "Run",
			shortLabel: "Run",
			busy: false,
			disabled: true,
			hint: "This plan uses the legacy step format and can no longer execute — re-create it in chat",
		};
	}

	return {
		kind: "run",
		label: "Run",
		shortLabel: "Run",
		busy: false,
		disabled: state.planningActive,
		hint: state.planningActive
			? "Wait for the planner to finish, then run"
			: "Run this test in a live browser",
	};
}
